import { Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Service global de gestion du clavier natif.
 *
 * - Publie l'état du clavier (`isVisible`, `keyboardHeight`) + la variable CSS
 *   `--keyboard-height`, consommée par les barres de rédaction et barres d'action
 *   fixes via la classe `.keyboard-lift` pour se positionner au-dessus du clavier.
 * - À l'ouverture du clavier, ramène le champ focalisé dans la zone visible
 *   au-dessus du clavier sans redimensionner la WebView.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardService {
  readonly isVisible = signal(false);
  readonly keyboardHeight = signal(0);

  /**
   * La compensation CSS ne vaut que là où la plateforme ne la fait pas elle-même.
   *
   * iOS (`resize: 'none'`) laisse la WebView pleine hauteur et ne déplace pas le
   * viewport visuel : c'est à l'app de remonter ses barres fixes de la hauteur du
   * clavier. Android, lui, décale déjà le viewport visuel pour amener le champ
   * focalisé au-dessus du clavier (`visualViewport.offsetTop`) — y ajouter la
   * compensation la double et projette la barre loin au-dessus du clavier.
   */
  private readonly compensatesKeyboard = Capacitor.getPlatform() !== 'android';

  async init(): Promise<void> {
    if (typeof window !== 'undefined') {
      window.addEventListener(
        'focusin',
        (event) => {
          const target = event.target as HTMLElement | null;
          if (this.isFormField(target)) {
            setTimeout(() => this.scrollActiveElementIntoView(target), 80);
            setTimeout(() => this.scrollActiveElementIntoView(target), 280);
          }
        },
        { passive: true }
      );
    }

    if (!Capacitor.isNativePlatform()) return;

    await Keyboard.addListener('keyboardWillShow', (info) => {
      this.isVisible.set(true);
      this.keyboardHeight.set(info.keyboardHeight);
      this.setKeyboardHeightVar(info.keyboardHeight);
    });

    await Keyboard.addListener('keyboardDidShow', () => {
      setTimeout(() => this.scrollActiveElementIntoView(), 50);
    });

    await Keyboard.addListener('keyboardWillHide', () => {
      this.isVisible.set(false);
      this.keyboardHeight.set(0);
      this.setKeyboardHeightVar(0);
    });
  }

  async dismiss(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Keyboard.hide();
    }
  }

  /**
   * Publie la compensation à appliquer dans `--keyboard-height` (utilisable en CSS).
   * Toujours 0 sur Android, où la plateforme remonte déjà le champ focalisé.
   */
  private setKeyboardHeightVar(height: number): void {
    const lift = this.compensatesKeyboard ? height : 0;
    document.documentElement.style.setProperty('--keyboard-height', `${lift}px`);
  }

  /** Vérifie si un élément est un champ de formulaire. */
  isFormField(el: HTMLElement | null): boolean {
    if (!el) return false;
    return (
      el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.tagName === 'SELECT' ||
      el.isContentEditable
    );
  }

  /** Ramène le champ de formulaire focalisé dans la zone visible au-dessus du clavier. */
  scrollActiveElementIntoView(customEl?: HTMLElement | null): void {
    const el = customEl || (document.activeElement as HTMLElement | null);
    if (!el || !this.isFormField(el)) return;

    const visibleHeight = (window.innerHeight || 800) - this.keyboardHeight();
    const rect = typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;

    if (!rect || rect.bottom > visibleHeight - 80 || rect.top < 65) {
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }
}
